/*
 * The search field's state (master-inbox plan, step 3b): settled operators as tokens, and the words
 * being typed. The words reach the API a beat after the last key; a token reaches it at once, since
 * it is a choice, not typing.
 *
 * Operators typed by hand ("from:jonas") are sent as typed, and the API parses them. They become
 * tokens when the rep commits the search with Enter, or when a saved view is opened: the next page
 * that answers that exact query carries the API's own parse (`search`), and the field takes its
 * operators as tokens and its words as the text. Nothing in the browser parses search syntax.
 */
import { useEffect, useState } from "react";
import type { InboxPage } from "../../src/inbox-v2/contract.ts";
import {
  composeQuery,
  type SearchOperator,
  type SearchToken,
  sameToken,
  withoutLastWord,
  withoutToken,
  withToken,
} from "./search-query.ts";

type Promotion = { q: string; quiet: boolean };

/** The API's parse as tokens, keeping the names the field already knew for the same operators. */
function promoted(operators: readonly SearchOperator[], known: readonly SearchToken[]) {
  return operators.map((operator) => ({
    ...operator,
    label: known.find((token) => sameToken(token, operator))?.label,
  }));
}

export function useSearch() {
  const [tokens, setTokens] = useState<SearchToken[]>([]);
  const [draft, setDraft] = useState("");
  // The words as the API was last asked for them: a beat behind typing, at once when set here.
  const [words, setWords] = useState("");
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setWords(draft.trim()), 250);
    return () => clearTimeout(timer);
  }, [draft]);
  const setNow = (text: string) => {
    setDraft(text);
    setWords(text.trim());
  };
  const q = composeQuery(tokens, words);
  return {
    tokens,
    /** What the field shows; empty while a saved view's query waits for the API's parse. */
    draft: promotion?.quiet ? "" : draft,
    q,
    /** The query has caught up with the field: no word typed since it was asked. */
    caughtUp: words === draft.trim() && promotion === null,
    setDraft,
    setTokens,
    /** A suggestion: its token in place of the word it completes, asked for at once. */
    choose: (token: SearchToken) => {
      setTokens((current) => withToken(current, token));
      setNow(withoutLastWord(draft));
    },
    remove: (token: SearchOperator) => setTokens((current) => withoutToken(current, token)),
    /** Enter: the typed operators become tokens once the API has read them. */
    commit: () => {
      setWords(draft.trim());
      setPromotion({ q: composeQuery(tokens, draft), quiet: false });
    },
    clear: () => {
      setTokens([]);
      setNow("");
      setPromotion(null);
    },
    clearWords: () => setNow(""),
    /** A saved view's query: sent whole at once, shown as tokens and words once parsed. */
    load: (text: string, settled: readonly SearchToken[]) => {
      setTokens([...settled]);
      setNow(text);
      setPromotion(text.trim() ? { q: composeQuery(settled, text), quiet: true } : null);
    },
    promotion,
    /**
     * Takes the API's parse of the committed query; `page` must answer `q` exactly. Null is an
     * answer with no page to parse (the agent list's refusal): the words stay as typed.
     */
    settle: (page: InboxPage | null) => {
      if (!page?.search) return setPromotion(null);
      setTokens(promoted(page.search.operators, tokens));
      setNow(page.search.text);
      setPromotion(null);
    },
  };
}

export type SearchState = ReturnType<typeof useSearch>;

/**
 * Settles a pending promotion when the answer to its query arrives: a page, or a refusal that carries
 * none. An answer kept on screen while the next one loads answers an older query and is not taken.
 */
export function useSettleSearch(
  search: SearchState,
  page: InboxPage | undefined,
  placeholder: boolean,
  refused = false,
): void {
  const pending = search.promotion?.q ?? null;
  const arrived = page !== undefined || refused;
  const answered = arrived && !placeholder && pending !== null && search.q === pending;
  useEffect(() => {
    if (answered) search.settle(page ?? null);
  });
}
